import React, { useState, useContext, useRef } from "react"
import PropTypes from "prop-types"

import Disenrollment from "./Disenrollment"

import { Button } from "primereact/button"
import { InputText } from "primereact/inputtext"
import { InputMask } from "primereact/inputmask"
import { Messages } from "primereact/messages"

import { UserContext } from "../../../../context/userContext"

import {
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  multiFactor,
  getAuth,
} from "firebase/auth"

import styles from "../../../../styles/TwoFA.module.css"

export default function Sms({
  reloadUser,
  setEnableMfaEnrolledFactor,
  enrolledFactor,
}) {
  const msgs = useRef(null)

  const userCtx = useContext(UserContext)

  const userPhone = userCtx?.session?.user?.staff_phone_number || ""
  const cleanedPhone = userPhone.replace(/^\+1/, "") // Removes +1 from the beginning

  const [phoneNumber, setPhoneNumber] = useState(cleanedPhone)
  const [verificationID, setVerificationID] = useState("")
  const [verificationCode, setVerificationCode] = useState("")
  const [verifySMSCode, setVerifySMSCode] = useState(false)
  const [isResending, setIsResending] = useState(false)

  const [reSignIn, setReSignIn] = useState(false)

  const auth = getAuth()
  const user = auth.currentUser

  const handleSendVerification = async () => {
    try {
      setIsResending(true)

      const multiFactorSession = await multiFactor(user).getSession()
      const phoneAuthProvider = new PhoneAuthProvider(auth)

      const formattedNumber = `+1${phoneNumber.replace(/\D/g, "")}`

      const verificationID = await phoneAuthProvider.verifyPhoneNumber(
        {
          phoneNumber: formattedNumber,
          session: multiFactorSession,
        },
        window.recaptchaVerifier
      )

      setVerificationID(verificationID)
      setVerifySMSCode(true)
      setIsResending(false)

      msgs.current.show([
        {
          severity: "info",
          detail: "Verification code sent.",
          sticky: false,
          closable: false,
        },
      ])
    } catch (error) {
      setIsResending(false)
      console.error("Error sending SMS:", error.code, error.message)
      if (error.code === "auth/requires-recent-login") {
        setReSignIn(true)
      }
      msgs.current.show([
        {
          severity: "error",
          detail: `Failed to send SMS: ${error.message}`,
          sticky: true,
          closable: false,
        },
      ])
    }
  }

  const handleVerifyCode = async () => {
    try {
      const cred = PhoneAuthProvider.credential(
        verificationID,
        verificationCode
      )

      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)
      await multiFactor(user).enroll(multiFactorAssertion, "Phone number")

      setVerifySMSCode(false)
      setPhoneNumber("")
    } catch (error) {
      console.error("Error enrolling second factor:", error)
      msgs.current.show([
        {
          severity: "error",
          detail: `Failed to enable two-factor authentication: ${error.message}`,
          sticky: true,
          closable: false,
        },
      ])
    } finally {
      msgs.current.clear()
      msgs.current.show([
        {
          severity: "success",
          detail: "Two-factor authentication enabled successfully.",
          sticky: true,
          closable: false,
        },
      ])
      await setEnableMfaEnrolledFactor("phone")
      setTimeout(() => {
        reloadUser()
      }, 2500)
    }
  }

  if (enrolledFactor) {
    const phoneLastDigits = enrolledFactor?.phoneNumber?.slice(-4)
    const enrollementDate = new Date(
      enrolledFactor.enrollmentTime
    ).toDateString()
    return (
      <>
        <div className={styles.setupBlock}>
          <h3>✅ SMS Two-factor Authentication already enabled</h3>
          <p>
            Enrolled on: <strong>{enrollementDate}</strong>
          </p>
          <p>
            Phone number ending in: <strong>{phoneLastDigits}</strong>
          </p>
        </div>
        <Disenrollment factorId={"phone"} />
      </>
    )
  }

  if (reSignIn) {
    return (
      <div className={styles.setupBlock}>
        <Messages ref={msgs} />
        <div className={styles.block}>
          <h3>
            Failed to continue with SMS enrollment. Please log in again.
            <button
              style={{ textDecoration: "underline" }}
              onClick={() => auth.signOut()}
            >
              Click here to re-login
            </button>
            .
          </h3>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.setupBlock}>
      <div id="recaptcha-container" />
      <Messages ref={msgs} />
      <h3>Configure your SMS Authentication</h3>
      <div className={styles.block}>
        <p>Enter your phone number to enable SMS authentication:</p>
        <InputMask
          value={phoneNumber}
          onChange={(e) => {
            setPhoneNumber(e.target.value)
          }}
          mask="(999) 999-9999"
          placeholder="(123) 456-7890"
          slotChar="_"
        />
        <Button
          type="button"
          onClick={handleSendVerification}
          disabled={verifySMSCode}
        >
          Verify Phone Number
        </Button>
      </div>

      {verifySMSCode && (
        <div className={styles.block}>
          <span className="p-float-label">
            <InputText
              id="verificationCode"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
            />
            <label>Verification Code</label>
          </span>
          <Button
            type="button"
            onClick={handleVerifyCode}
            style={{ marginTop: "10px" }}
          >
            Enter Verification Code
          </Button>
          <p style={{ marginTop: "5px" }}>
            Didn't receive a code?{" "}
            <button
              type="button"
              onClick={handleSendVerification}
              disabled={isResending}
              style={{
                color: "blue",
                background: "none",
                border: "none",
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {isResending ? "Resending..." : "Resend Code"}
            </button>
          </p>
        </div>
      )}
    </div>
  )
}

Sms.propTypes = {
  reloadUser: PropTypes.func.isRequired,
  setEnableMfaEnrolledFactor: PropTypes.func.isRequired,
  enrolledFactor: PropTypes.object,
}
